import { mapGetters, mapActions } from 'vuex'

import parseData from '@/utils/parseData'
import tooltips from '@/utils/tooltips'
import ChartComponent from '@/components/Chart/Chart.vue'
import ChatGPT from '@/components/ChatGPT/ChatGPT.vue'

export default {
  name: 'Report',
  data () {
    return {
      isLoading: {
        gscData: false,
        keywordCluster: false,
        pageCluster: false,
        autocompleteArticles: false
      },

      chartData: null,
      chartOptions: {
        scales: {
          yAxes: [{
            type: 'linear',
            display: false,
            position: 'left',
            id: 'y-axis-1',
            min: 0,
            ticks: {
              beginAtZero: true
            }
          },
          {
            type: 'linear',
            display: false,
            position: 'left',
            id: 'y-axis-2',
            min: 0,
            ticks: {
              beginAtZero: true
            }
          },
          {
            type: 'linear',
            display: false,
            position: 'right',
            id: 'y-axis-3',
            min: 0,
            ticks: {
              beginAtZero: true
            }
          },
          {
            type: 'linear',
            display: false,
            position: 'left',
            id: 'y-axis-4',
            min: 0,
            ticks: {
              reverse: true,
              beginAtZero: true
            }
          }],
          xAxes: [{
            type: 'time',
            time: {
              unit: 'week',
              isoWeekday: true,
              displayFormats: {
                day: 'MMM D, YYYY',
                week: 'MMM YYYY',
                month: 'MMM YYYY'
              }
            }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          position: 'nearest'
        },
        legend: {
          display: false
        },
        animation: {
          duration: 1000,
          easing: 'easeInOutQuart'
        },
        responsive: true,
        maintainAspectRatio: false
      },

      date: [],
      queryFilter: null,
      queryFilterType: 'includingRegex',
      pageFilter: null,
      pageFilterType: 'includingRegex',
      onlyContentGeckoPages: false,
      filterTypes: [
        { text: 'Including regex', value: 'includingRegex', icon: 'mdi-plus' },
        { text: 'Excluding regex', value: 'excludingRegex', icon: 'mdi-minus' }
      ],
      countryOptions: [
        { text: 'All countries', value: 'all' },
        { text: 'Target country only', value: 'target' }
      ],
      country: 'all',
      aggregationOptions: [
        { text: 'Daily', value: 'daily' },
        { text: 'Weekly', value: 'weekly' },
        { text: 'Monthly', value: 'monthly' }
      ],
      aggregation: 'weekly',
      compare: false,
      actions: [
        { text: 'Leave as-is', value: 'leave' },
        { text: 'Merge', value: 'merge' },
        { text: 'Delete', value: 'delete' },
        { text: 'Update', value: 'update' },
        { text: 'Not enough data', value: 'not-enough-data' }
      ],

      tablePages: {
        isLoading: false,
        headers: [
          { text: 'URL', value: 'keys[0]' },
          { text: 'Cluster', value: 'cluster' },
          { text: 'Impressions', value: 'impressions' },
          { text: 'Clicks', value: 'clicks' }
        ],
        sortBy: 'impressions',
        sortDesc: true
      },

      tableKeywords: {
        isLoading: false,
        headers: [
          { text: 'Keyword', value: 'keys[0]' },
          { text: 'Cluster', value: 'cluster' },
          { text: 'Impressions', value: 'impressions' },
          { text: 'Clicks', value: 'clicks' }
        ],
        sortBy: 'impressions',
        sortDesc: true
      },

      selectedMetrics: [
        { text: 'Impressions', value: 'impressions' },
        { text: 'Clicks', value: 'clicks' }
      ],
      metrics: [
        { text: 'Impressions', value: 'impressions' },
        { text: 'Clicks', value: 'clicks' },
        { text: 'CTR', value: 'ctr' },
        { text: 'Rank', value: 'position' }
      ],

      keywordClusterInfo: [],
      pageClusterInfo: [],
      showCopilot: false,
      isSharingReport: false,
      sharedContentplanName: null
    }
  },
  computed: {
    ...mapGetters([
      'topics',
      'articles',
      'activeContentplan',
      'activeWorkspace',
      'gsc',
      'pages',
      'keywords'
    ]),
    workspaceId () {
      return parseInt(this.$route.params.workspaceId)
    },
    contentplanId () {
      return parseInt(this.$route.params.contentplanId)
    },
    sharingKey () {
      return this.$route.params.sharingKey || ''
    },
    isSharedView () {
      return Boolean(this.sharingKey)
    },
    contentplanName () {
      return this.activeContentplan?.name || this.sharedContentplanName
    },
    remainingCredits () {
      return this.activeWorkspace?.remaining_article_credits
    },
    gscImpressions () {
      if (!this.gsc.byDate || this.gscErrorResponse) return
      return this.gsc.byDate.map(x => x.impressions).reduce((a, b) => a + b, 0)
    },
    gscClicks () {
      if (!this.gsc.byDate || this.gscErrorResponse) return
      return this.gsc.byDate.map(x => x.clicks).reduce((a, b) => a + b, 0)
    },
    gscCtr () {
      if (!this.gsc.byDate || this.gscErrorResponse) return
      return parseData(this.gscClicks / this.gscImpressions * 100, 1) + '%'
    },
    gscPosition () {
      if (!this.gsc.byDate || this.gscErrorResponse) return
      let positionArray = this.gsc.byDate.map(x => x.position)
      const impressionArray = this.gsc.byDate.map(x => x.impressions)
      if (positionArray.length > 1) {
        positionArray = positionArray.map(x => x === 'N/a' ? 100 : x)
        return parseData(positionArray.reduce(function (r, a, i) { return r + a * impressionArray[i] }, 0) / impressionArray.reduce((a, b) => a + b, 0), 1)
      }

      return positionArray[0] === 'N/a' ? 'N/a' : parseData(positionArray[0], 1)
    },
    gscErrorResponse () {
      if (!this.gsc) return false
      if (typeof this.gsc.byDate === 'string') {
        console.log(this.gsc.byDate)
        return this.gsc.byDate
      }
      return false
    },
    gscQueriesSorted () {
      if (!this.gsc.byQuery) return
      return [...this.gsc.byQuery].sort((a, b) => b.impressions - a.impressions)
    },
    gscPagesSorted () {
      if (!this.gsc.byPage) return
      return [...this.gsc.byPage].sort((a, b) => b.impressions - a.impressions)
    },
    canLoadMoreQuery () {
      return Number.isInteger(this.gsc?.pagination?.queryNextCursor)
    },
    canLoadMorePage () {
      return Number.isInteger(this.gsc?.pagination?.pageNextCursor)
    },
    pageTableHeaders () {
      return [
        { text: 'URL', value: 'keys[0]' },
        ...(!this.isSharedView ? [{ text: 'Cluster', value: 'cluster' }] : []),
        ...this.selectedMetrics
      ]
    },
    keywordTableHeaders () {
      return [
        { text: 'Keyword', value: 'keys[0]' },
        ...(!this.isSharedView ? [{ text: 'Cluster', value: 'cluster' }] : []),
        ...this.selectedMetrics
      ]
    }
  },
  watch: {
    async $route () {
      await this.loadData()
    }
  },
  methods: {
    parseData,
    tooltips,
    ...mapActions([
      'keywordGet',
      'pageGet',
      'getGSCReportBundle',
      'setSnackbar',
      'contentplanGetOne',
      'contentplanPut'
    ]),

    // RENDER DATA

    async loadData () {
      this.isLoading.gscData = true
      document.title = 'Report | ContentGecko'
      this.articlePage = 1

      // set this.date to a range of the last 30 days in the format YYYY-MM-DD
      this.setDates(360)

      try {
        if (!this.isSharedView) {
          // Load contentplan first to check for GSC integration
          await this.contentplanGetOne({ contentplanId: this.contentplanId })

          if (!this.activeContentplan?.gsc_property) {
            this.setSnackbar({ open: true, text: 'No GSC integration', type: 'error' })
            this.isLoading.gscData = false
            return
          }
        }

        const response = await this.fetchGSCBundle({
          sections: ['DATE', 'QUERY', 'PAGE']
        })
        if (this.isSharedView) this.sharedContentplanName = response?.contentplan?.name || null
      } catch (error) {
        console.log(error)
      } finally {
        if (!this.gscErrorResponse) {
          await this.calcChartData()
        }
        this.isLoading.gscData = false
      }
    },
    async refreshGSCData () {
      this.isLoading.gscData = true

      // Ensure contentplan is loaded
      if (!this.isSharedView && !this.activeContentplan) {
        await this.contentplanGetOne({ contentplanId: this.contentplanId })
      }

      if (!this.isSharedView && !this.activeContentplan?.gsc_property) {
        this.setSnackbar({ open: true, text: 'No GSC integration', type: 'error' })
        this.isLoading.gscData = false
        return
      }

      try {
        await this.fetchGSCBundle({
          sections: ['DATE', 'QUERY', 'PAGE'],
          pageFilter: this.pageFilter,
          keywordFilter: this.queryFilter
        })
      } catch (error) {
        console.log(error)
      } finally {
        if (!this.gscErrorResponse) {
          await this.calcChartData()
        }
        this.isLoading.gscData = false
      }
    },
    buildGSCBundlePayload ({
      sections = ['DATE', 'QUERY', 'PAGE'],
      pageFilter = null,
      keywordFilter = null,
      queryCursor = 0,
      pageCursor = 0
    } = {}) {
      const resolvedPageFilter = pageFilter === null ? this.pageFilter : pageFilter
      const resolvedKeywordFilter = keywordFilter === null ? this.queryFilter : keywordFilter

      const payload = {
        contentplanId: this.contentplanId,
        startDate: this.date[0],
        endDate: this.date[1],
        aggregation: this.aggregation,
        pages: resolvedPageFilter,
        pageFilterType: this.pageFilterType,
        keywords: resolvedKeywordFilter,
        queryFilterType: this.queryFilterType,
        country: this.country,
        onlyContentGeckoPages: this.onlyContentGeckoPages,
        sections,
        queryCursor,
        pageCursor,
        queryLimit: 50,
        pageLimit: 50
      }

      if (this.sharingKey) payload.key = this.sharingKey
      return payload
    },
    async fetchGSCBundle ({
      sections = ['DATE', 'QUERY', 'PAGE'],
      pageFilter = null,
      keywordFilter = null,
      queryCursor = 0,
      pageCursor = 0,
      appendQuery = false,
      appendPage = false
    } = {}) {
      const payload = this.buildGSCBundlePayload({
        sections,
        pageFilter,
        keywordFilter,
        queryCursor,
        pageCursor
      })

      return this.getGSCReportBundle({
        ...payload,
        appendQuery,
        appendPage
      })
    },
    async loadMoreQueries () {
      const queryNextCursor = this.gsc?.pagination?.queryNextCursor
      if (!Number.isInteger(queryNextCursor)) return

      this.tableKeywords.isLoading = true

      try {
        await this.fetchGSCBundle({
          sections: ['QUERY'],
          queryCursor: queryNextCursor,
          pageCursor: this.gsc?.pagination?.pageNextCursor || 0,
          appendQuery: true
        })
      } catch (error) {
        console.log(error)
      } finally {
        this.tableKeywords.isLoading = false
      }
    },
    async loadMorePages () {
      const pageNextCursor = this.gsc?.pagination?.pageNextCursor
      if (!Number.isInteger(pageNextCursor)) return

      this.tablePages.isLoading = true

      try {
        await this.fetchGSCBundle({
          sections: ['PAGE'],
          queryCursor: this.gsc?.pagination?.queryNextCursor || 0,
          pageCursor: pageNextCursor,
          appendPage: true
        })
      } catch (error) {
        console.log(error)
      } finally {
        this.tablePages.isLoading = false
      }
    },
    async calcChartData () {
      if (!this.gsc.byDate) {
        this.isLoading.gscData = false
        return
      }

      const dateArray = this.gsc.byDate?.map(x => x.keys[0])

      this.chartData = {
        labels: dateArray,
        datasets: []
      }

      if (this.selectedMetrics.some(x => x.value === 'impressions')) {
        this.chartData.datasets.push({
          label: 'Impressions',
          backgroundColor: 'white',
          borderColor: '#5e35b1',
          fill: false,
          data: this.gsc.byDate?.map(x => x.impressions),
          yAxisID: 'y-axis-1'
        })
      }

      if (this.selectedMetrics.some(x => x.value === 'clicks')) {
        this.chartData.datasets.push({
          label: 'Clicks',
          backgroundColor: 'white',
          borderColor: '#4285f4',
          fill: false,
          data: this.gsc.byDate?.map(x => x.clicks),
          yAxisID: 'y-axis-2'
        })
      }

      if (this.selectedMetrics.some(x => x.value === 'ctr')) {
        this.chartData.datasets.push({
          label: 'CTR',
          backgroundColor: 'white',
          borderColor: '#21897b',
          fill: false,
          data: this.gsc.byDate?.map(x => parseData(x.ctr * 100, 1)),
          yAxisID: 'y-axis-3'
        })
      }

      if (this.selectedMetrics.some(x => x.value === 'position')) {
        this.chartData.datasets.push({
          label: 'Rank',
          backgroundColor: 'white',
          borderColor: '#e8710b',
          fill: false,
          data: this.gsc.byDate?.map(x => parseData(x.position, 1)),
          yAxisID: 'y-axis-4'
        })
      }
      this.isLoading.gscData = false
    },
    metricIsActive (metric) {
      return this.selectedMetrics.some(x => x.value.includes(metric))
    },
    async metricChange (metric) {
      this.isLoading.gscData = true
      if (this.metricIsActive(metric)) {
        this.selectedMetrics = this.selectedMetrics.filter(x => !x.value.includes(metric))
      } else {
        this.selectedMetrics.push(this.metrics.find(x => x.value === metric))
      }
      // wait before calculating chart data
      setTimeout(async () => {
        await this.calcChartData()
      }, 100)
    },
    async populateKeywordClusterInfo (items) {
      if (this.isSharedView || this.isLoading.keywordCluster || !items) return
      this.isLoading.keywordCluster = true

      try {
        const promises = items
          .filter(kw => !this.keywordClusterInfo.some(x => x.keyword === kw.keys[0]))
          .map(async kw => {
            try {
              const result = await this.keywordGet({
                contentplanId: this.contentplanId,
                filters: [{ field: 'keyword', op: 'eq', value: kw.keys[0] }],
                straightReturn: true
              })
              const keyword = result?.items?.[0]
              if (keyword) this.keywordClusterInfo.push(keyword)
            } catch (error) {
              console.log(error)
            }
          })
        await Promise.all(promises)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading.keywordCluster = false
      }
    },
    keywordCluster (item) {
      const emptyResult = {
        article_id: null,
        title: null,
        excluded: null
      }

      const cluster = this.keywordClusterInfo.find(x => x.keyword === item.keys[0])
      if (!cluster) return emptyResult

      return {
        article_id: cluster.article_id,
        title: cluster.title,
        excluded: cluster.excluded
      }
    },
    async populatePageClusterInfo (items) {
      if (this.isSharedView || this.isLoading.pageCluster || !items) return
      this.isLoading.pageCluster = true

      try {
        const promises = items
          .filter(page => !this.pageClusterInfo.some(x => x.url === page.keys[0]))
          .map(async page => {
            try {
              const result = await this.pageGet({
                contentplanId: this.contentplanId,
                filters: [{ field: 'url', op: 'eq', value: page.keys[0] }]
              })
              const pageResult = result?.items?.[0]
              if (pageResult) this.pageClusterInfo.push(pageResult)
            } catch (error) {
              console.log(error)
            }
          })
        await Promise.all(promises)
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading.pageCluster = false
      }
    },
    pageCluster (item) {
      const emptyResult = {
        article_id: null,
        title: null,
        excluded: null
      }

      const cluster = this.pageClusterInfo.find(x => x.url === item.keys[0])
      if (!cluster) return emptyResult

      return {
        article_id: cluster.article_id,
        title: cluster.title,
        excluded: cluster.excluded
      }
    },
    formatDate (date) {
      const year = date.getFullYear()
      // JavaScript months are 0-based, so +1 is added to get the correct month number
      const month = ('0' + (date.getMonth() + 1)).slice(-2)
      const day = ('0' + date.getDate()).slice(-2)
      return `${year}-${month}-${day}`
    },
    setDates (days) {
      const today = new Date()
      const dayOfWeek = today.getDay() // 0 = Sunday, 1 = Monday, etc.

      // Calculate days since most recent Sunday
      const daysSinceSunday = dayOfWeek === 0 ? 0 : dayOfWeek

      // Determine end date (last full Sunday)
      // A week is full if more than 3 days have passed since Sunday (accounting for GSC 3-day delay)
      const endDate = new Date(today)
      if (daysSinceSunday > 3) {
        // Use this week's Sunday
        endDate.setDate(today.getDate() - daysSinceSunday)
      } else {
        // Use last week's Sunday
        endDate.setDate(today.getDate() - daysSinceSunday - 7)
      }

      // Calculate start date
      const startDate = new Date(endDate)
      startDate.setDate(endDate.getDate() - days)

      this.date = [this.formatDate(startDate), this.formatDate(endDate)]
    },
    async shareReport () {
      if (this.isSharedView) return
      this.isSharingReport = true

      try {
        let sharingKey = this.activeContentplan?.sharing

        if (!sharingKey) {
          const response = await this.contentplanPut({
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId,
            action: 'share',
            share: true
          })
          sharingKey = response?.sharing
        }

        if (!sharingKey) throw new Error('Report sharing key was not created')

        const { href } = this.$router.resolve({
          name: 'ReportShared',
          params: {
            workspaceId: this.workspaceId,
            contentplanId: this.contentplanId,
            sharingKey
          }
        })
        const url = `${window.location.origin}${href}`
        await navigator.clipboard.writeText(url)
        await this.contentplanGetOne({
          contentplanId: this.contentplanId
        })
        this.setSnackbar({ open: true, text: 'Report link copied to clipboard', type: 'success' })
      } catch {
        this.setSnackbar({ open: true, text: 'Failed to share report', type: 'error' })
      } finally {
        this.isSharingReport = false
      }
    },
    toggleCopilot () {
      this.showCopilot = !this.showCopilot
    },
    changeAggregation (aggregation) {
      this.aggregation = aggregation
      this.chartOptions.scales.xAxes[0].time.unit = aggregation === 'daily' ? 'day' : aggregation === 'weekly' ? 'week' : 'month'
      console.log('aggregation', this.aggregation, this.chartOptions.scales.xAxes[0].time.unit)
      this.refreshGSCData()
    }
  },
  components: { ChartComponent, ChatGPT },
  async mounted () {
    await this.loadData()
  }
}
